import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Box, Typography, Paper, Chip, Button, IconButton, TextField,
  Tabs, Tab, LinearProgress, Snackbar, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  BottomNavigation, BottomNavigationAction, CircularProgress,
  Switch, FormControlLabel
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
  Warning as WarnIcon,
  Search as SearchIcon,
  LocalShipping as TruckIcon,
  Inventory as BoxIcon,
  AssignmentReturn as ReturnIcon,
  NotificationsActive as BellIcon,
  QrCode as QrIcon,
  QrCodeScanner as ScanIcon,
  Home as HomeIcon,
  ListAlt as ListIcon,
  FileDownload as DownloadIcon,
  Share as ShareIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Close as CloseIcon,
  PhotoCamera as PhotoIcon,
  Delete as TrashIcon,
} from '@mui/icons-material';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api';
const STATUS_META = {
  GENERADA:   { label: 'Generada',   color: '#6366f1', light: '#eef2ff', icon: '📋', order: 0 },
  GENERATED:  { label: 'Generada',   color: '#6366f1', light: '#eef2ff', icon: '📋', order: 0 },
  PENDIENTE:  { label: 'Pendiente',  color: '#f59e0b', light: '#fffbeb', icon: '⏳', order: 0 },
  CARGANDO:   { label: 'Cargando',   color: '#f59e0b', light: '#fffbeb', icon: '🚛', order: 1 },
  EN_EVENTO:  { label: 'En Evento',  color: '#10b981', light: '#ecfdf5', icon: '🎉', order: 2 },
  RETORNANDO: { label: 'Retornando', color: '#3b82f6', light: '#eff6ff', icon: '📦', order: 3 },
  COMPLETADA: { label: 'Completada', color: '#6b7280', light: '#f3f4f6', icon: '✅', order: 4 },
  CANCELADA:  { label: 'Cancelada',  color: '#ef4444', light: '#fef2f2', icon: '❌', order: 4 },
};
const PROGRESS_LIST = ['GENERADA', 'CARGANDO', 'EN_EVENTO', 'RETORNANDO', 'COMPLETADA'];

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('es-CO', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const getEventDate = (o) => {
  const s = o.at || o.date || o.eventDate || o.schedule?.start;
  if (!s) return null;
  try { return new Date(s); } catch { return null; }
};

const playSound = (type) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator(); const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination); gain.gain.value = 0.12;
    if (type === 'success') { osc.frequency.value = 880; osc.start(); setTimeout(() => { osc.frequency.value = 1100; }, 80); }
    else if (type === 'error') { osc.frequency.value = 440; osc.type = 'sawtooth'; osc.start(); }
    else { osc.frequency.value = 660; osc.start(); }
    setTimeout(() => { osc.stop(); ctx.close(); }, 200);
  } catch {}
};
const vibrate = (ms = 15) => { try { navigator.vibrate?.(ms); } catch {} };

function ImgBox({ src, fallback, size = 52, bgColor = '#6366f118' }) {
  const [err, setErr] = useState(false);
  if (!src || err) return (
    <Box sx={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: bgColor, color: bgColor === '#6366f118' ? '#6366f1' : '#fff', fontSize: size * 0.28, fontWeight: 800, borderRadius: 1.5, flexShrink: 0 }}>{fallback}</Box>
  );
  return <img src={src} referrerPolicy="no-referrer" alt="" onError={() => setErr(true)} style={{ width: size, height: size, objectFit: 'cover', display: 'block' }} />;
}

function QRDialog({ open, onClose, orderId }) {
  const [qr, setQr] = useState('');
  useEffect(() => {
    if (!open || !orderId) return;
    let active = true;
    import('qrcode').then(m => {
      const url = `${window.location.origin}/bodega/acceso?order=${orderId}`;
      m.default.toDataURL(url, { width: 280, margin: 2, color: { dark: '#1e293b', light: '#fff' } }).then(d => { if (active) setQr(d); });
    });
    return () => { active = false; };
  }, [open, orderId]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 700 }}>{orderId}</DialogTitle>
      <DialogContent sx={{ textAlign: 'center', pb: 3 }}>
        {qr ? <img src={qr} alt="QR" style={{ width: 200, height: 200, margin: '12px auto', display: 'block' }} /> : <CircularProgress sx={{ my: 4 }} />}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => { const a = document.createElement('a'); a.href = qr; a.download = `${orderId}.png`; a.click(); }} sx={{ borderRadius: 2 }}>Descargar QR</Button>
        <Button variant="outlined" startIcon={<ShareIcon />} onClick={() => { if (navigator.share) navigator.share({ title: orderId, text: `${window.location.origin}/bodega/acceso?order=${orderId}` }); else navigator.clipboard?.writeText(`${window.location.origin}/bodega/acceso?order=${orderId}`); }} sx={{ borderRadius: 2 }}>Compartir</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function BodegaModule({ orders = [], setOrders, readOnly = false }) {
  const [view, setView] = useState('dashboard');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [qrDialog, setQrDialog] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTab, setFilterTab] = useState(0);
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [salidaForm, setSalidaForm] = useState({ registradoPor: '', items: [] });
  const [entradaForm, setEntradaForm] = useState({ registradoPor: '', items: [], obs: '' });
  const [reqPhoto, setReqPhoto] = useState(true);
  const [genRemLoading, setGenRemLoading] = useState(false); // "Generar Remisión" (3-sep-2026)
  const [remIvaIncluido, setRemIvaIncluido] = useState(true); // toggle IVA incluido/no incluido (4-sep-2026)
  const [remisiones, setRemisiones] = useState([]); // remisiones generadas de la orden abierta (4-sep-2026)

  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const notify = useCallback((msg, severity = 'success') => { vibrate(); setSnack({ open: true, msg, severity }); }, []);

  useEffect(() => {
    if (!selectedOrder?.items) return;
    const allItems = (selectedOrder.salidaData?.items || selectedOrder.items || []);
    setSalidaForm({ registradoPor: '', items: allItems.map(it => ({ ...it, checked: false, obs: '', accesoriosCheck: (it.accesorios||[]).map(a => ({ nombre: a, checked: false })) })) });
    setEntradaForm({ registradoPor: '', items: allItems.map(it => ({ ...it, condition: 'BIEN', obs: '', photo: null, accesoriosCond: (it.accesorios||[]).map(a => ({ nombre: a, condition: 'BIEN' })) })), obs: '' });
  }, [selectedOrder?.id]);

  // Remisiones ya generadas para la orden abierta (4-sep-2026)
  useEffect(() => {
    if (!selectedOrder?.quoteNumber) { setRemisiones([]); return; }
    fetch(`${API_BASE}/remisiones?quoteNumber=${encodeURIComponent(selectedOrder.quoteNumber)}`)
      .then(r => r.json()).then(d => setRemisiones(d.ok ? d.remisiones : []))
      .catch(() => setRemisiones([]));
  }, [selectedOrder?.id, selectedOrder?.quoteNumber]);

  const { ordersHoy, proximas, historial, stats } = useMemo(() => {
    const hoy = []; const prox = []; const hist = [];
    for (const o of orders) {
      const d = getEventDate(o);
      const isToday = d && d.toDateString() === today.toDateString();
      const done = ['COMPLETADA', 'CANCELADA'].includes(o.status);
      if (isToday && !done) hoy.push(o);
      if (done) hist.push(o);
      else prox.push(o);
    }
    prox.sort((a,b) => (a.at||'') > (b.at||'') ? 1 : -1);
    hist.sort((a,b) => (b.at||'') > (a.at||'') ? 1 : -1);
    const filtroEstado = filterStatus !== 'ALL' ? o => o.status === filterStatus : () => true;
    const filtroTexto = searchTerm.trim() ? o => {
      const t = searchTerm.toLowerCase();
      return (o.quoteNumber||'').toLowerCase().includes(t) || (o.id||'').toLowerCase().includes(t) || (o.destination?.city||'').toLowerCase().includes(t) || ((o.items||[]).some(it => (it.name||'').toLowerCase().includes(t)));
    } : () => true;
    return {
      ordersHoy: hoy,
      proximas: prox.filter(filtroEstado).filter(filtroTexto),
      historial: hist.filter(filtroEstado).filter(filtroTexto),
      stats: {
        total: orders.length,
        completadas: orders.filter(o => o.status === 'COMPLETADA').length,
        enEvento: orders.filter(o => o.status === 'EN_EVENTO').length,
        pendientes: orders.filter(o => !o.status || ['GENERADA','GENERATED','PENDIENTE'].includes(o.status)).length,
        alertas: orders.filter(o => o.reminder?.active).length,
        hoy: hoy.length
      }
    };
  }, [orders, filterStatus, searchTerm, today]);

  const currentList = filterTab === 0 ? proximas : filterTab === 1 ? ordersHoy : historial;
  const getSM = (s) => STATUS_META[s] || STATUS_META.GENERADA;
  const getProgressIdx = (s) => { const i = PROGRESS_LIST.indexOf(s); return i >= 0 ? i : 0; };

  const saveOrder = useCallback(async (updated) => {
    if (setOrders) setOrders(prev => prev.map(o => o.id === updated.id ? updated : o));
    try {
      const res = await fetch(`${API_BASE}/orders/warehouse/${updated.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updated)
      });
      if (!res.ok) throw new Error();
      playSound('success');
    } catch { notify('Error al guardar', 'error'); }
  }, [setOrders, notify]);

  // "Generar Remisión" (3-sep-2026) — comprobante de entrega con valores, antes de la
  // factura. Manual: el que despacha decide cuándo generarla, no se dispara sola.
  const handleGenerarRemision = useCallback(async (warehouseOrderId, ivaIncluido) => {
    setGenRemLoading(true);
    try {
      const res = await fetch(`${API_BASE}/remisiones`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ warehouseOrderId, ivaIncluido }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'No se pudo generar la remisión');
      notify(`Remisión ${data.remisionNumber} generada`);
      setRemisiones(prev => [data.remision, ...prev]);
      window.open(`${API_BASE}/remisiones/${data.remisionNumber}/pdf`, '_blank');
    } catch (e) {
      notify(e.message || 'Error al generar la remisión', 'error');
    } finally {
      setGenRemLoading(false);
    }
  }, [notify]);

  // Eliminar una remisión ya generada (4-sep-2026)
  const handleEliminarRemision = useCallback(async (remisionNumber) => {
    if (!window.confirm(`¿Eliminar la remisión ${remisionNumber}? Esta acción no se puede deshacer.`)) return;
    try {
      const res = await fetch(`${API_BASE}/remisiones/${remisionNumber}`, { method: 'DELETE' });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'No se pudo eliminar la remisión');
      setRemisiones(prev => prev.filter(r => r.remisionNumber !== remisionNumber));
      notify(`Remisión ${remisionNumber} eliminada`);
    } catch (e) {
      notify(e.message || 'Error al eliminar la remisión', 'error');
    }
  }, [notify]);

  const handleSalida = useCallback(async () => {
    if (!salidaForm.registradoPor.trim()) { notify('Indica quién cargó', 'warning'); return; }
    if (salidaForm.items.every(i => !i.checked)) { notify('Marca al menos un equipo', 'warning'); return; }
    const o = selectedOrder; if (!o) return;
    const updated = { ...o, status: 'EN_EVENTO', salidaData: { ...salidaForm, registradoEn: new Date().toISOString() } };
    await saveOrder(updated);
    setSelectedOrder(updated);
    setView('detail');
    notify(`Salida: ${salidaForm.registradoPor}`);
  }, [salidaForm, selectedOrder, saveOrder, notify]);

  const handleEntrada = useCallback(async () => {
    if (!entradaForm.registradoPor.trim()) { notify('Indica quién verificó', 'warning'); return; }
    if (reqPhoto) {
      const sinFoto = entradaForm.items.filter(i => !i.photo);
      if (sinFoto.length > 0) { notify(`Foto requerida: ${sinFoto.map(i => i.name).join(', ')}`, 'warning'); return; }
    }
    const o = selectedOrder; if (!o) return;
    const hasIssues = entradaForm.items.some(i => i.condition !== 'BIEN');
    const reminder = hasIssues ? {
      active: true,
      reason: entradaForm.items.filter(i => i.condition !== 'BIEN').map(i => `${i.name}: ${i.condition}${i.obs ? ` — ${i.obs}` : ''}`).join('; '),
      nextAt: (() => { let d = new Date(); d.setDate(d.getDate() + 1); while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return d; })().toISOString(),
      history: []
    } : null;
    const updated = { ...o, status: 'COMPLETADA', entradaData: { ...entradaForm, registradoEn: new Date().toISOString() }, reminder };
    await saveOrder(updated);
    setSelectedOrder(updated);
    setView('detail');
    notify(hasIssues ? 'Entrada con novedades' : 'Entrada sin novedades');
  }, [entradaForm, selectedOrder, reqPhoto, saveOrder, notify]);

  const handleResolveReminder = useCallback(async () => {
    const o = selectedOrder; if (!o) return;
    const updated = { ...o, reminder: { ...o.reminder, active: false, resolvedAt: new Date().toISOString() } };
    await saveOrder(updated); setSelectedOrder(updated); notify('Recordatorio resuelto');
  }, [selectedOrder, saveOrder, notify]);

  const handleReprogramReminder = useCallback(async () => {
    const o = selectedOrder; if (!o) return;
    let d = new Date(); let count = 0;
    while (count < 2) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0 && d.getDay() !== 6) count++; }
    d.setHours(9, 0, 0, 0);
    const updated = { ...o, reminder: { ...o.reminder, nextAt: d.toISOString(), history: [...(o.reminder?.history || []), { reprogramadoEn: new Date().toISOString(), nuevoAt: d.toISOString() }] } };
    await saveOrder(updated); setSelectedOrder(updated); notify('Recordatorio +48h hábiles');
  }, [selectedOrder, saveOrder, notify]);

  // ═══ SCANNER — prioritario, antes de cualquier vista ═══
  if (scannerOpen) return (
    <ScannerErrorBoundary onClose={() => setScannerOpen(false)}>
      <QRScannerCompo onScan={(url) => {
        setScannerOpen(false);
        const match = url.match(/[?&]order=([^&]+)/);
        if (match) { const found = orders.find(o => o.id === match[1]); if (found) { setSelectedOrder(found); setView('detail'); } else notify('Orden no encontrada', 'error'); }
        else notify('QR no válido', 'error');
      }} onClose={() => setScannerOpen(false)} />
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2 }}>{snack.msg}</Alert>
      </Snackbar>
    </ScannerErrorBoundary>
  );

  // ═══ DASHBOARD ═══
  const renderDashboard = () => {
    const hoySalidas = orders.filter(o => {
      const d = getEventDate(o);
      return d && d.toDateString() === today.toDateString() && !['COMPLETADA', 'CANCELADA'].includes(o.status);
    });
    return (
      <Box sx={{ pb: 10 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>Panel de Bodega</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
          {[
            { label: 'Hoy', count: stats.hoy, color: '#6366f1', bg: '#eef2ff', icon: '📅' },
            { label: 'Pendientes', count: stats.pendientes, color: '#f59e0b', bg: '#fffbeb', icon: '📋' },
            { label: 'En Evento', count: stats.enEvento, color: '#10b981', bg: '#ecfdf5', icon: '🎉' },
            { label: 'Alertas', count: stats.alertas, color: '#ef4444', bg: '#fef2f2', icon: '🔔' },
          ].map(s => (
            <Paper key={s.label} elevation={0} sx={{ p: 1.5, borderRadius: 3, bgcolor: s.bg, border: `1px solid ${s.color}22`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: s.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>{s.icon}</Box>
              <Box><Typography variant="caption" sx={{ color: s.color, fontWeight: 600, display: 'block' }}>{s.label}</Typography><Typography variant="h5" sx={{ fontWeight: 800, color: s.color, lineHeight: 1.2 }}>{s.count}</Typography></Box>
            </Paper>
          ))}
        </Box>
        <Button variant="contained" size="large" fullWidth onClick={() => { setScannerOpen(true); }} sx={{ borderRadius: 3, py: 2, fontWeight: 700, mb: 1.5, fontSize: '1rem', bgcolor: '#0f172a', '&:hover': { bgcolor: '#020617' } }}>
          <ScanIcon sx={{ mr: 1.5, fontSize: 24 }} /> Escanear QR
        </Button>
        <Button variant="contained" size="large" fullWidth onClick={() => { setView('list'); setFilterTab(0); }} sx={{ borderRadius: 3, py: 1.5, fontWeight: 700, mb: 2, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}>
          <ListIcon sx={{ mr: 1 }} /> Ver órdenes ({stats.pendientes + stats.enEvento} activas)
        </Button>
        {stats.alertas > 0 && (
          <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fef2f2', border: '1px solid #fecaca', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><BellIcon sx={{ color: '#ef4444', fontSize: 20 }} /><Typography variant="body2" sx={{ fontWeight: 600, color: '#dc2626' }}>{stats.alertas} recordatorio(s) activo(s)</Typography></Box>
          </Paper>
        )}
        {hoySalidas.length > 0 && (
          <Box><Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}><TruckIcon sx={{ fontSize: 18 }} /> Salidas de hoy</Typography>
            {hoySalidas.map(o => { const sm = getSM(o.status); return (
              <Paper key={o.id} elevation={1} sx={{ p: 1.5, mb: 1, borderRadius: 2, cursor: 'pointer', borderLeft: `5px solid ${sm.color}`, '&:active': { bgcolor: '#f1f5f9' } }}
                onClick={() => { setSelectedOrder(o); setView('detail'); }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box><Typography variant="body2" sx={{ fontWeight: 700 }}>{o.id}</Typography><Typography variant="caption" color="text.secondary">{o.destination?.city || '—'} · {o.items?.length || 0} equipos</Typography></Box>
                  <Chip label={sm.label} size="small" sx={{ bgcolor: sm.color + '18', color: sm.color, fontWeight: 700, fontSize: '0.7rem', borderRadius: 1 }} />
                </Box>
              </Paper>
            );})}
          </Box>
        )}
        <BottomNav view={view} setView={setView} setScannerOpen={setScannerOpen} />
      </Box>
    );
  };

  // ═══ LIST ═══
  const renderList = () => (
    <Box sx={{ pb: 10 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <IconButton onClick={() => setView('dashboard')} sx={{ bgcolor: '#f1f5f9' }}><BackIcon /></IconButton>
        <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>Órdenes</Typography>
        <IconButton onClick={() => setScannerOpen(true)} sx={{ bgcolor: '#f1f5f9' }}><ScanIcon /></IconButton>
      </Box>
      <TextField fullWidth size="small" placeholder="Buscar orden, cotización o ciudad..." value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: '#9ca3af' }} />, sx: { borderRadius: 3, bgcolor: '#fff' } }}
        sx={{ mb: 1.5 }} />
      <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, overflow: 'auto', pb: 0.5, '&::-webkit-scrollbar': { display: 'none' } }}>
        {[
          { key: 'ALL', label: 'Todas', color: '#6b7280' },
          { key: 'GENERADA', label: 'Generada', color: '#6366f1' },
          { key: 'CARGANDO', label: 'Cargando', color: '#f59e0b' },
          { key: 'EN_EVENTO', label: 'Evento', color: '#10b981' },
          { key: 'RETORNANDO', label: 'Retornando', color: '#3b82f6' },
          { key: 'COMPLETADA', label: 'Completada', color: '#6b7280' },
        ].map(s => (
          <Chip key={s.key} label={s.label} size="small" onClick={() => setFilterStatus(s.key)}
            sx={{ borderRadius: 2, fontWeight: 600, flexShrink: 0, bgcolor: filterStatus === s.key ? s.color + '18' : '#f1f5f9', color: filterStatus === s.key ? s.color : '#6b7280', border: filterStatus === s.key ? `1.5px solid ${s.color}` : '1.5px solid transparent' }} />
        ))}
      </Box>
      <Tabs value={filterTab} onChange={(_, v) => setFilterTab(v)} sx={{ mb: 1.5, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: '0.8rem', fontWeight: 600, textTransform: 'none' }, '& .MuiTabs-indicator': { borderRadius: 2, height: 3 } }}>
        <Tab label={`Activas (${proximas.length})`} />
        <Tab label={`Hoy (${ordersHoy.length})`} />
        <Tab label={`Historial (${historial.length})`} />
      </Tabs>
      {currentList.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', color: '#9ca3af', borderRadius: 3, bgcolor: '#f8fafc' }}>
          <BoxIcon sx={{ fontSize: 48, opacity: 0.2 }} />
          <Typography variant="body2" sx={{ mt: 1 }}>No hay órdenes en esta categoría</Typography>
          {filterStatus !== 'ALL' && <Button size="small" sx={{ mt: 1 }} onClick={() => setFilterStatus('ALL')}>Limpiar filtro</Button>}
          {searchTerm && <Button size="small" sx={{ mt: 0.5 }} onClick={() => setSearchTerm('')}>Limpiar búsqueda</Button>}
        </Paper>
      ) : currentList.map(o => {
        const sm = getSM(o.status); const items = o.items || [];
        return (
          <Paper key={o.id} elevation={1} sx={{ mb: 1.5, borderRadius: 2, overflow: 'hidden', cursor: 'pointer', borderLeft: `5px solid ${sm.color}`, '&:active': { transform: 'scale(0.98)' }, transition: 'transform 0.15s' }}
            onClick={() => { setSelectedOrder(o); setView('detail'); }}>
            <Box sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.3 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {o.clientName && <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.clientName}</Typography>}
                  <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 500 }}>{o.id}</Typography>
                </Box>
                <Chip label={sm.label} size="small" sx={{ bgcolor: sm.color + '18', color: sm.color, fontWeight: 700, fontSize: '0.7rem', borderRadius: 1, height: 22, ml: 1, flexShrink: 0 }} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                <LocationIcon sx={{ fontSize: 13, color: '#9ca3af' }} />
                <Typography variant="caption" color="text.secondary">{o.destination?.city || '—'}</Typography>
                {o.reminder?.active && <WarnIcon sx={{ fontSize: 13, color: '#ef4444', ml: 1 }} />}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary"><CalendarIcon sx={{ fontSize: 12, mr: 0.3, verticalAlign: 'text-bottom' }} />{o.eventDate ? fmtDate(o.eventDate) : (o.at ? fmtDate(o.at) : '—')}</Typography>
                <Typography variant="caption" color="text.secondary">· {items.length} equipo(s)</Typography>
              </Box>
            </Box>
          </Paper>
        );
      })}
      <BottomNav view={view} setView={setView} setScannerOpen={setScannerOpen} />
    </Box>
  );

  // ═══ DETAIL ═══
  const renderDetail = () => {
    if (!selectedOrder) return null;
    const o = selectedOrder; const sm = getSM(o.status);
    const pIdx = getProgressIdx(o.status);
    const items = (o.salidaData?.items || o.items || []); const dest = o.destination || {};
    const canSalida = !o.salidaData && !['COMPLETADA', 'CANCELADA'].includes(o.status);
    const canEntrada = !o.entradaData && o.status === 'EN_EVENTO';
    return (
      <Box sx={{ pb: 10 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <IconButton onClick={() => setView('list')} sx={{ bgcolor: '#f1f5f9' }}><BackIcon /></IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>{o.id}</Typography>
          <IconButton onClick={() => setQrDialog(o.id)} sx={{ bgcolor: '#f1f5f9' }}><QrIcon /></IconButton>
        </Box>
        <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: sm.light, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: sm.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#fff' }}>{sm.icon}</Box>
            <Box><Typography variant="body2" sx={{ fontWeight: 700, color: sm.color }}>{sm.label}</Typography><Typography variant="caption" color="text.secondary">{o.quoteNumber || '—'}</Typography></Box>
          </Box>
          <Typography variant="caption" color="text.secondary">{o.at ? fmtDate(o.at) : '—'}</Typography>
        </Paper>
        <Box sx={{ mb: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, px: 0.5 }}>
            {PROGRESS_LIST.map((s, i) => {
              const meta = STATUS_META[s]; const active = i <= pIdx;
              return (
                <Box key={s} sx={{ textAlign: 'center', flex: 1 }}>
                  <Box sx={{ width: 24, height: 24, borderRadius: '50%', mx: 'auto', mb: 0.3, bgcolor: active ? meta.color : '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: active ? '#fff' : '#9ca3af', fontSize: 11, fontWeight: 700 }}>
                    {i < pIdx ? '✓' : i + 1}
                  </Box>
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', color: active ? meta.color : '#9ca3af', fontWeight: active ? 700 : 400, display: { xs: 'none', sm: 'block' } }}>{meta.label}</Typography>
                </Box>
              );
            })}
          </Box>
          <LinearProgress variant="determinate" value={Math.min((pIdx / (PROGRESS_LIST.length - 1)) * 100, 100)} sx={{ height: 5, borderRadius: 3, bgcolor: '#e5e7eb', '& .MuiLinearProgress-bar': { bgcolor: sm.color, borderRadius: 3 } }} />
        </Box>
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <Box><Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>Fecha</Typography><Typography variant="body2">{o.at ? fmtDate(o.at) : '—'}</Typography></Box>
            <Box><Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>Horario</Typography><Typography variant="body2">{(o.schedule?.start || '—')}{(o.schedule?.end ? ` — ${o.schedule.end}` : '')}</Typography></Box>
            <Box sx={{ gridColumn: 'span 2' }}><Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>Destino</Typography><Typography variant="body2">{dest.city || '—'}{dest.address ? ` — ${dest.address}` : ''}</Typography></Box>
          </Box>
        </Paper>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}><BoxIcon sx={{ fontSize: 18 }} /> Equipos ({items.length})</Typography>
        {items.map((it, i) => (
          <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: it.accesorios?.length || it.voltaje || it.observaciones ? 1 : 0 }}>
              <ImgBox src={it.imageUrl} fallback={i + 1} size={52} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{it.name || it.productName}</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.3 }}>
                  <Typography variant="caption" color="text.secondary">Cód: {it.code || '—'}</Typography>
                  <Typography variant="caption" color="text.secondary">Cant: {it.qty || 1}</Typography>
                  {it.medidas && <Typography variant="caption" color="text.secondary">{it.medidas}</Typography>}
                  {it.operarios > 0 && <Typography variant="caption" color="text.secondary">👤 {it.operarios} op.</Typography>}
                  {it.voltaje && <Typography variant="caption" color="text.secondary">⚡ {it.voltaje}{it.amperaje ? ` / ${it.amperaje}` : ''}</Typography>}
                </Box>
              </Box>
            </Box>
            {it.accesorios?.length > 0 && (
              <Box sx={{ pl: 5.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#6b7280', display: 'block', mb: 0.5 }}>Accesorios:</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {it.accesorios.map((a, ai) => (
                    <Chip key={ai} label={a} size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#f1f5f9', color: '#475569', borderRadius: 1 }} />
                  ))}
                </Box>
              </Box>
            )}
            {it.observaciones && (
              <Box sx={{ pl: 5.5, mt: 0.5 }}>
                <Typography variant="caption" sx={{ color: '#f59e0b', fontStyle: 'italic' }}>⚠ {it.observaciones}</Typography>
              </Box>
            )}
          </Paper>
        ))}
        {o.salidaData ? (
          <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, mb: 1.5, mt: 2, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderLeft: '4px solid #22c55e' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><CheckIcon sx={{ color: '#16a34a', fontSize: 20 }} /><Box><Typography variant="body2" sx={{ fontWeight: 600, color: '#16a34a' }}>Salida registrada</Typography><Typography variant="caption" color="text.secondary">Por {o.salidaData.registradoPor || '—'} · {o.salidaData.registradoEn ? fmtDate(o.salidaData.registradoEn) : ''}</Typography></Box></Box>
            <FormControlLabel sx={{ display: 'block', ml: 0, mb: 0.5 }}
              control={<Switch size="small" checked={remIvaIncluido} onChange={e => setRemIvaIncluido(e.target.checked)} />}
              label={<Typography variant="caption">{remIvaIncluido ? 'IVA incluido' : 'IVA no incluido'}</Typography>} />
            <Button size="small" variant="outlined" startIcon={genRemLoading ? <CircularProgress size={14} /> : <DownloadIcon />}
              disabled={genRemLoading} onClick={() => handleGenerarRemision(o.id, remIvaIncluido)}
              sx={{ borderRadius: 2, fontWeight: 600, borderColor: '#16a34a', color: '#16a34a', '&:hover': { borderColor: '#15803d', bgcolor: '#f0fdf4' } }}>
              Generar Remisión
            </Button>
            {!!remisiones.length && (
              <Box sx={{ mt: 1 }}>
                {remisiones.map(r => (
                  <Box key={r.remisionNumber} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => window.open(`${API_BASE}/remisiones/${r.remisionNumber}/pdf`, '_blank')}>
                      {r.remisionNumber}{r.ivaIncluido === false ? ' (IVA no incluido)' : ''}
                    </Typography>
                    <IconButton size="small" onClick={() => handleEliminarRemision(r.remisionNumber)}>
                      <TrashIcon fontSize="small" sx={{ color: '#ef4444' }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        ) : canSalida ? (
          <Button variant="contained" fullWidth size="large" onClick={() => setView('salida')} sx={{ borderRadius: 3, py: 1.8, fontWeight: 700, mb: 1.5, mt: 2, fontSize: '1rem', bgcolor: '#d97706', '&:hover': { bgcolor: '#b45309' } }}>
            <TruckIcon sx={{ mr: 1.5, fontSize: 22 }} /> Registrar Salida
          </Button>
        ) : null}
        {o.entradaData ? (
          <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, mb: 1.5, bgcolor: '#eef2ff', border: '1px solid #c7d2fe', borderLeft: '4px solid #6366f1' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CheckIcon sx={{ color: '#6366f1', fontSize: 20 }} /><Box><Typography variant="body2" sx={{ fontWeight: 600, color: '#6366f1' }}>Entrada registrada</Typography><Typography variant="caption" color="text.secondary">Por {o.entradaData.registradoPor || '—'} · {o.entradaData.registradoEn ? fmtDate(o.entradaData.registradoEn) : ''}</Typography></Box></Box>
          </Paper>
        ) : canEntrada ? (
          <Button variant="contained" fullWidth size="large" onClick={() => setView('entrada')} sx={{ borderRadius: 3, py: 1.8, fontWeight: 700, mb: 1.5, fontSize: '1rem', bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}>
            <ReturnIcon sx={{ mr: 1.5, fontSize: 22 }} /> Registrar Entrada
          </Button>
        ) : null}
        {o.reminder?.active && (
          <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderLeft: '4px solid #ef4444', mb: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><BellIcon sx={{ color: '#dc2626', fontSize: 20 }} /><Typography variant="body2" sx={{ fontWeight: 600, color: '#dc2626' }}>Recordatorio activo</Typography></Box>
            <Typography variant="body2" sx={{ mb: 0.5, color: '#7f1d1d' }}>{o.reminder.reason}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Próximo: {o.reminder.nextAt ? fmtDate(o.reminder.nextAt) : '—'}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="contained" color="error" onClick={handleResolveReminder} sx={{ borderRadius: 2, fontWeight: 600 }}>Resolver</Button>
              <Button size="small" variant="outlined" color="error" onClick={handleReprogramReminder} sx={{ borderRadius: 2, fontWeight: 600 }}>Reprogramar</Button>
            </Box>
          </Paper>
        )}
        <BottomNav view={view} setView={setView} setScannerOpen={setScannerOpen} />
      </Box>
    );
  };

  // ═══ SALIDA ═══
  const renderSalida = () => {
    if (!selectedOrder) return null;
    const o = selectedOrder;
    return (
      <Box sx={{ pb: 10 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <IconButton onClick={() => setView('detail')} sx={{ bgcolor: '#f1f5f9' }}><BackIcon /></IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>Salida</Typography>
          <Chip label={o.id} size="small" sx={{ fontWeight: 600, borderRadius: 1 }} />
        </Box>
        <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fffbeb', border: '1px solid #fde68a', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <TruckIcon sx={{ color: '#d97706' }} /><Typography variant="body2" sx={{ fontWeight: 600, color: '#92400e' }}>Marca los equipos que se cargan</Typography>
        </Paper>
        <TextField fullWidth label="Cargado por" value={salidaForm.registradoPor}
          onChange={e => setSalidaForm(p => ({ ...p, registradoPor: e.target.value }))}
          sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Equipos</Typography>
        {salidaForm.items.map((it, i) => {
          const totalAcc = it.accesoriosCheck?.length || 0;
          const checkedAcc = it.accesoriosCheck?.filter(a => a.checked).length || 0;
          return (
            <Paper key={i} variant="outlined" sx={{ mb: 1.5, borderRadius: 2, overflow: 'hidden', borderColor: it.checked ? '#86efac' : '#e5e7eb', bgcolor: it.checked ? '#f0fdf4' : '#fff' }}>
              <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', '&:active': { opacity: 0.8 } }}
                onClick={() => { vibrate(); setSalidaForm(p => ({ ...p, items: p.items.map((x, j) => j === i ? { ...x, checked: !x.checked, accesoriosCheck: (x.accesoriosCheck||[]).map(a => ({ ...a, checked: !x.checked })) } : x) })); }}>
                <Box sx={{ width: 44, height: 44, borderRadius: 1.5, flexShrink: 0, overflow: 'hidden', border: it.checked ? '2px solid #16a34a' : '2px solid #e5e7eb', position: 'relative', transition: 'border-color 0.15s' }}>
                  <ImgBox src={it.imageUrl} fallback={it.checked ? '✓' : ''} size={44} bgColor={it.checked ? '#16a34a' : '#e5e7eb'} />
                  {it.checked && it.imageUrl && <Box sx={{ position: 'absolute', inset: 0, bgcolor: '#16a34a88', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 20, fontWeight: 800 }}>✓</Box>}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{it.name || it.productName}</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">Cant: {it.qty || 1}</Typography>
                    {totalAcc > 0 && <Typography variant="caption" sx={{ color: checkedAcc === totalAcc ? '#16a34a' : '#f59e0b', fontWeight: 600 }}>{checkedAcc}/{totalAcc} accesorios</Typography>}
                  </Box>
                </Box>
              </Box>
              {it.accesoriosCheck?.length > 0 && (
                <Box sx={{ px: 1.5, pb: 1.5, pt: 0, borderTop: '1px solid #f0fdf4' }}>
                  {it.accesoriosCheck.map((ac, ai) => (
                    <Box key={ai} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, cursor: 'pointer' }}
                      onClick={() => { vibrate(); setSalidaForm(p => ({ ...p, items: p.items.map((x, j) => j === i ? { ...x, accesoriosCheck: x.accesoriosCheck.map((a, k) => k === ai ? { ...a, checked: !a.checked } : a) } : x) })); }}>
                      <Box sx={{ width: 20, height: 20, borderRadius: 0.8, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: ac.checked ? '#16a34a' : '#e5e7eb', color: '#fff', fontSize: 11, fontWeight: 700 }}>{ac.checked ? '✓' : ''}</Box>
                      <Typography variant="caption" sx={{ color: ac.checked ? '#15803d' : '#64748b' }}>{ac.nombre}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          );
        })}
        <Button variant="contained" fullWidth size="large" disabled={!salidaForm.registradoPor.trim()}
          onClick={handleSalida} sx={{ borderRadius: 3, py: 1.8, fontWeight: 700, mt: 2, fontSize: '1rem', bgcolor: '#d97706', '&:hover': { bgcolor: '#b45309' } }}>
          <TruckIcon sx={{ mr: 1 }} /> Confirmar Salida
        </Button>
        <BottomNav view={view} setView={setView} setScannerOpen={setScannerOpen} />
      </Box>
    );
  };

  // ═══ ENTRADA ═══
  const renderEntrada = () => {
    if (!selectedOrder) return null;
    const o = selectedOrder;
    return (
      <Box sx={{ pb: 10 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <IconButton onClick={() => setView('detail')} sx={{ bgcolor: '#f1f5f9' }}><BackIcon /></IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>Entrada</Typography>
          <Chip label={o.id} size="small" sx={{ fontWeight: 600, borderRadius: 1 }} />
        </Box>
        <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#eef2ff', border: '1px solid #c7d2fe', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReturnIcon sx={{ color: '#6366f1' }} /><Typography variant="body2" sx={{ fontWeight: 600, color: '#4338ca' }}>Verifica el estado de cada equipo</Typography>
        </Paper>
        <TextField fullWidth label="Verificado por" value={entradaForm.registradoPor}
          onChange={e => setEntradaForm(p => ({ ...p, registradoPor: e.target.value }))}
          sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
        <FormControlLabel control={<Switch checked={reqPhoto} onChange={(_, v) => setReqPhoto(v)} size="small" />} label="Foto obligatoria por equipo" sx={{ mb: 1.5, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }} />
        {entradaForm.items.map((it, i) => (
          <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              {it.imageUrl && <Box sx={{ width: 52, height: 52, borderRadius: 1.5, flexShrink: 0, overflow: 'hidden', border: '1px solid #e5e7eb' }}><ImgBox src={it.imageUrl} size={52} fallback="" /></Box>}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{it.name || it.productName}</Typography>
                {it.code && <Typography variant="caption" color="text.secondary">{it.code}</Typography>}
              </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, mb: 1 }}>
              {[
                { v: 'BIEN', color: '#16a34a', bg: '#f0fdf4', icon: '✅' },
                { v: 'DAÑADO', color: '#d97706', bg: '#fffbeb', icon: '⚠️' },
                { v: 'FALTA', color: '#dc2626', bg: '#fef2f2', icon: '❌' },
                { v: 'INCOMPLETO', color: '#7c3aed', bg: '#f5f3ff', icon: '🔶' },
              ].map(opt => (
                <Button key={opt.v} fullWidth onClick={() => { vibrate(); setEntradaForm(p => ({ ...p, items: p.items.map((x, j) => j === i ? { ...x, condition: opt.v } : x) })); }}
                  sx={{ borderRadius: 2, py: 1.3, fontWeight: 700, fontSize: '0.8rem', bgcolor: it.condition === opt.v ? opt.bg : '#f8fafc', color: it.condition === opt.v ? opt.color : '#6b7280', border: it.condition === opt.v ? `2px solid ${opt.color}` : '2px solid #e5e7eb', '&:hover': { bgcolor: opt.bg } }}>
                  {opt.icon} {opt.v.charAt(0) + opt.v.slice(1).toLowerCase()}
                </Button>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <TextField size="small" placeholder="Observación" value={it.obs}
                onChange={e => setEntradaForm(p => ({ ...p, items: p.items.map((x, j) => j === i ? { ...x, obs: e.target.value } : x) }))}
                sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.8rem', borderRadius: 2 }, flex: 1, minWidth: 140 }} />
              <Button size="small" variant={it.photo ? 'contained' : 'outlined'} startIcon={<PhotoIcon />} component="label"
                color={it.photo ? 'success' : 'primary'} sx={{ borderRadius: 2, flexShrink: 0 }}>
                {it.photo ? 'Listo' : 'Foto'}
                <input hidden accept="image/*" capture="environment" type="file"
                  onChange={(e) => { const f = e.target.files?.[0]; if (!f) return; const r = new FileReader(); r.onload = (ev) => setEntradaForm(p => ({ ...p, items: p.items.map((x, j) => j === i ? { ...x, photo: ev.target.result } : x) })); r.readAsDataURL(f); }} />
              </Button>
            </Box>
            {it.photo && (
              <Box sx={{ mt: 1, position: 'relative', display: 'inline-block' }}>
                <img src={it.photo} alt="" style={{ height: 80, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                <IconButton size="small" sx={{ position: 'absolute', top: -6, right: -6, bgcolor: '#fff', boxShadow: 1 }} onClick={() => setEntradaForm(p => ({ ...p, items: p.items.map((x, j) => j === i ? { ...x, photo: null } : x) }))}><CloseIcon fontSize="small" /></IconButton>
              </Box>
            )}
            {it.accesoriosCond?.length > 0 && (
              <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid #f1f5f9' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#6b7280', display: 'block', mb: 0.8 }}>Accesorios:</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {it.accesoriosCond.map((ac, ai) => {
                    const condColors = { BIEN: '#16a34a', DAÑADO: '#d97706', FALTA: '#dc2626', INCOMPLETO: '#7c3aed' };
                    const c = condColors[ac.condition] || '#6b7280';
                    return (
                      <Box key={ai} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="caption" sx={{ flex: 1, minWidth: 100 }}>{ac.nombre}</Typography>
                        {['BIEN','DAÑADO','FALTA'].map(opt => (
                          <Button key={opt} size="small" onClick={() => { vibrate(); setEntradaForm(p => ({ ...p, items: p.items.map((x, j) => j === i ? { ...x, accesoriosCond: x.accesoriosCond.map((a, k) => k === ai ? { ...a, condition: opt } : a) } : x) })); }}
                            sx={{ minWidth: 0, px: 0.8, py: 0.2, fontSize: '0.65rem', fontWeight: 700, borderRadius: 1, bgcolor: ac.condition === opt ? condColors[opt] + '20' : '#f8fafc', color: ac.condition === opt ? condColors[opt] : '#9ca3af', border: ac.condition === opt ? `1.5px solid ${condColors[opt]}` : '1.5px solid #e5e7eb' }}>
                            {opt.charAt(0) + opt.slice(1).toLowerCase()}
                          </Button>
                        ))}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Paper>
        ))}
        <TextField fullWidth multiline rows={2} label="Observación general" value={entradaForm.obs}
          onChange={e => setEntradaForm(p => ({ ...p, obs: e.target.value }))}
          sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
        <Button variant="contained" fullWidth size="large" disabled={!entradaForm.registradoPor.trim()}
          onClick={handleEntrada} sx={{ borderRadius: 3, py: 1.8, fontWeight: 700, fontSize: '1rem', bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}>
          <ReturnIcon sx={{ mr: 1 }} /> Confirmar Entrada
        </Button>
        <BottomNav view={view} setView={setView} setScannerOpen={setScannerOpen} />
      </Box>
    );
  };

  // ═══ RENDER PRINCIPAL ═══
  let content;
  if (view === 'dashboard') content = renderDashboard();
  else if (view === 'list') content = renderList();
  else if (view === 'detail') content = renderDetail();
  else if (view === 'salida') content = renderSalida();
  else if (view === 'entrada') content = renderEntrada();
  else content = renderDashboard();

  return (
    <>
      {content}
      {qrDialog && <QRDialog open={!!qrDialog} onClose={() => setQrDialog(null)} orderId={qrDialog} />}
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2, minWidth: 200 }}>{snack.msg}</Alert>
      </Snackbar>
    </>
  );
}

class ScannerErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error: error?.message || 'Error en escáner' }; }
  render() {
    if (this.state.error) return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error" sx={{ fontWeight: 600, mb: 2 }}>{this.state.error}</Typography>
        <Button variant="contained" onClick={() => this.props.onClose?.()} sx={{ borderRadius: 2 }}>Cerrar</Button>
      </Box>
    );
    return this.props.children;
  }
}

function QRScannerCompo({ onScan, onClose }) {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const scannerDiv = useRef(null);
  const instance = useRef(null);
  useEffect(() => {
    let active = true;
    import('html5-qrcode').then(mod => {
      if (!active || !scannerDiv.current) return;
      setLoading(false);
      try {
        const s = new mod.Html5Qrcode('qr-reader-box');
        instance.current = s;
        s.start({ facingMode: 'environment' }, { fps: 8, qrbox: { width: 240, height: 240 } },
          (decoded) => { if (active) { s.stop().catch(()=>{}); vibrate(); onScan(decoded); } }, () => {}
        ).catch((err) => { if (active) { setError(err?.message?.includes('NotAllowedError') ? 'Permiso de cámara denegado' : 'Cámara no disponible o sin permisos'); } });
      } catch (e) { if (active) setError('Error al iniciar cámara'); }
    }).catch(() => { if (active) { setLoading(false); setError('Librería QR no disponible'); } });
    return () => { try { active = false; if (instance.current) instance.current.stop().catch(()=>{}); } catch {} };
  }, []);

  if (error) return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography color="error" sx={{ mb: 2, fontWeight: 600 }}>{error}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Verifica los permisos de cámara en la configuración del dispositivo</Typography>
      <Button variant="contained" onClick={onClose} sx={{ borderRadius: 2 }}>Cerrar</Button>
    </Box>
  );

  return (
    <Box sx={{ px: 2, pt: 2, maxWidth: 500, mx: 'auto', pb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>Escanear QR</Typography>
        <IconButton onClick={onClose} sx={{ bgcolor: '#f1f5f9' }}><CloseIcon /></IconButton>
      </Box>
      {loading && <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /><Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Iniciando cámara...</Typography></Box>}
      <Box id="qr-reader-box" ref={scannerDiv} sx={{ borderRadius: 3, overflow: 'hidden', display: loading ? 'none' : 'block', '& video': { width: '100%', borderRadius: 3 } }} />
      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block', mt: 1.5 }}>Apunta la cámara al QR de la orden</Typography>
      <Button fullWidth variant="outlined" onClick={onClose} sx={{ mt: 2, borderRadius: 2, py: 1.5 }}>Cancelar</Button>
    </Box>
  );
}

function BottomNav({ view, setView, setScannerOpen }) {
  const active = view === 'dashboard' ? 'dashboard' : (['list', 'detail', 'salida', 'entrada'].includes(view) ? 'list' : '');
  return (
    <Paper elevation={4} sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100, bgcolor: '#fff', borderTop: '1px solid #e5e7eb', display: { xs: 'block', sm: 'none' } }}>
      <BottomNavigation showLabels value={active} onChange={(_, v) => { if (v === 'scan') setScannerOpen(true); else setView(v); }}>
        <BottomNavigationAction label="Panel" value="dashboard" icon={<HomeIcon />} />
        <BottomNavigationAction label="Órdenes" value="list" icon={<ListIcon />} />
        <BottomNavigationAction label="Escanear" value="scan" icon={<ScanIcon />} />
      </BottomNavigation>
    </Paper>
  );
}
